import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TicketIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { supabase, sessionManager, getAdminStatusCached } from './lib/supabase';
import Header from './Header';
import WhatsAppButton from './WhatsAppButton';

const TAB_ALL = 'all';
const TAB_UPCOMING = 'upcoming';
const TAB_PAST = 'past';

function MyBookingsPage() {
  const [user, setUser] = useState(null);
  const [, setUserProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(TAB_UPCOMING);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          navigate('/');
          return;
        }

        setUser(session.user);
        await fetchUserProfile(session.user.email);
      } catch (error) {
        console.error('Error checking user access:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        navigate('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchUserProfile = async (email) => {
    const { data, error } = await getAdminStatusCached(email);
    if (!error && data?.[0]) {
      setUserProfile(data[0]);
    }
  };

  const fetchUserBookings = useCallback(async () => {
    if (!user) return;

    try {
      await sessionManager.withValidSession(async () => {
        const { data, error } = await supabase
          .from('bookings')
          .select(`
            id,
            customer_name,
            customer_phone,
            customer_email,
            booking_date,
            slot_time,
            total_amount,
            status,
            created_at,
            booking_slots,
            courts (
              name
            )
          `)
          .eq('user_id', user.id)
          .order('booking_date', { ascending: false })
          .order('created_at', { ascending: false });

        if (!error) {
          setBookings(data || []);
        }
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (error.message.includes('Session invalid')) {
        // Session expired — redirect to home instead of reload loop
        navigate('/');
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      fetchUserBookings();
    }
  }, [user, fetchUserBookings]);

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const hour24 = parseInt(hours);
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isUpcoming = (booking) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const bookingDate = new Date(booking.booking_date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate >= today && booking.status !== 'cancelled';
  };

  const filteredBookings = useMemo(() => {
    let filtered = bookings;

    if (activeTab === TAB_UPCOMING) {
      filtered = filtered.filter(isUpcoming);
    } else if (activeTab === TAB_PAST) {
      filtered = filtered.filter(b => !isUpcoming(b));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(b =>
        (b.courts?.name || '').toLowerCase().includes(q) ||
        (b.customer_name || '').toLowerCase().includes(q) ||
        formatDate(b.booking_date).toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [bookings, activeTab, searchQuery]);

  const stats = useMemo(() => ({
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    confirmed: bookings.filter(b => b.status === 'approved' || b.status === 'confirmed').length,
    totalSpent: bookings
      .filter(b => b.status === 'approved' || b.status === 'confirmed')
      .reduce((sum, b) => sum + parseFloat(b.total_amount || 0), 0),
  }), [bookings]);

  const statusConfig = {
    pending: {
      label: 'Pending Approval',
      icon: ClockIcon,
      badge: 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
      accent: 'border-l-amber-400',
      dot: 'bg-amber-400',
    },
    approved: {
      label: 'Approved & Booked',
      icon: CheckCircleIcon,
      badge: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
      accent: 'border-l-emerald-500',
      dot: 'bg-emerald-500',
    },
    confirmed: {
      label: 'Confirmed',
      icon: CheckCircleIcon,
      badge: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
      accent: 'border-l-emerald-500',
      dot: 'bg-emerald-500',
    },
    cancelled: {
      label: 'Cancelled',
      icon: ExclamationTriangleIcon,
      badge: 'bg-red-50 text-red-700 ring-1 ring-red-600/20',
      accent: 'border-l-red-400',
      dot: 'bg-red-400',
    },
  };

  const getStatus = (status) => statusConfig[status] || {
    label: 'Unknown',
    icon: ExclamationTriangleIcon,
    badge: 'bg-gray-50 text-gray-700 ring-1 ring-gray-600/20',
    accent: 'border-l-gray-400',
    dot: 'bg-gray-400',
  };

  const tabs = [
    { id: TAB_UPCOMING, label: 'Upcoming', count: bookings.filter(isUpcoming).length },
    { id: TAB_PAST, label: 'Past', count: bookings.filter(b => !isUpcoming(b)).length },
    { id: TAB_ALL, label: 'All', count: bookings.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-200 border-t-green-600 mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Loading your bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/20" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10" />
        </div>

        <div className="relative container mx-auto px-4 pt-8 pb-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="animate-smooth-rise">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">My Bookings</h1>
              <p className="text-green-200 text-lg">Track and manage your court reservations</p>
            </div>

            {/* Compact Stats */}
            <div className="flex flex-wrap gap-3 animate-smooth-rise animate-delay-200">
              {[
                { label: 'Total', value: stats.total, bg: 'bg-white/15' },
                { label: 'Pending', value: stats.pending, bg: 'bg-amber-400/20' },
                { label: 'Confirmed', value: stats.confirmed, bg: 'bg-emerald-400/20' },
                { label: 'Spent', value: `₹${stats.totalSpent.toLocaleString()}`, bg: 'bg-white/15' },
              ].map((s) => (
                <div key={s.label} className={`${s.bg} backdrop-blur-sm rounded-xl px-4 py-2.5 text-white min-w-[90px]`}>
                  <div className="text-xs font-medium text-green-200 uppercase tracking-wider">{s.label}</div>
                  <div className="text-xl font-bold mt-0.5">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tabs & Search Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3">
            {/* Tabs */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-white text-green-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search bookings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-64 pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {filteredBookings.length === 0 ? (
          /* Empty State */
          <div className="animate-smooth-rise">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center max-w-lg mx-auto">
              <div className="bg-green-50 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <TicketIcon className="h-10 w-10 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {bookings.length === 0
                  ? 'No bookings yet'
                  : 'No matching bookings'}
              </h3>
              <p className="text-gray-500 mb-8 leading-relaxed">
                {bookings.length === 0
                  ? "You haven't booked any courts yet. Reserve your first slot and start playing!"
                  : `No bookings found for "${activeTab}" with your search. Try a different filter.`}
              </p>
              {bookings.length === 0 && (
                <Link
                  to="/booking"
                  className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
                >
                  <CalendarIcon className="h-5 w-5 mr-2" />
                  Book Your First Court
                </Link>
              )}
            </div>
          </div>
        ) : (
          /* Booking Cards */
          <div className="space-y-4">
            {filteredBookings.map((booking, index) => {
              const cfg = getStatus(booking.status);
              const StatusIcon = cfg.icon;
              const upcoming = isUpcoming(booking);

              return (
                <div
                  key={booking.id}
                  className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 border-l-4 ${cfg.accent} animate-smooth-rise`}
                  style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
                >
                  <div className="p-5 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      {/* Left */}
                      <div className="flex-1 min-w-0">
                        {/* Top Row: Court + Badge */}
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900 truncate">
                            {booking.courts?.name || 'Court'}
                          </h3>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.badge}`}>
                            <StatusIcon className="h-3.5 w-3.5" />
                            {cfg.label}
                          </span>
                          {upcoming && booking.status !== 'cancelled' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 ring-1 ring-blue-500/20">
                              Upcoming
                            </span>
                          )}
                        </div>

                        {/* Details Grid */}
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                          <div className="flex items-center gap-1.5">
                            <CalendarIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span>{formatDate(booking.booking_date)}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span>
                              {booking.slot_time 
                                ? formatTime(booking.slot_time)
                                : booking.booking_slots && booking.booking_slots.length > 0
                                ? booking.booking_slots.map(slot => formatTime(slot.slot_time)).join(', ')
                                : 'No slots recorded'
                              }
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 text-gray-400">
                            <span className="text-xs">Booked {new Date(booking.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Right - Amount */}
                      <div className="flex sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0.5 flex-shrink-0">
                        <span className="text-2xl font-bold text-gray-900">
                          ₹{parseFloat(booking.total_amount || 0).toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400 font-medium">
                          {booking.booking_slots?.length || 1} slot{(booking.booking_slots?.length || 1) > 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Book Another CTA */}
        {bookings.length > 0 && (
          <div className="mt-10 text-center animate-smooth-rise animate-delay-300">
            <Link
              to="/booking"
              className="inline-flex items-center px-7 py-3.5 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all shadow-sm hover:shadow-md group"
            >
              <CalendarIcon className="h-5 w-5 mr-2 group-hover:scale-110 transition-transform" />
              Book Another Court
            </Link>
          </div>
        )}
      </main>

      <WhatsAppButton />
    </div>
  );
}

export default MyBookingsPage;
